import { useTranslation } from "react-i18next";
import { Link, useParams, useSearchParams } from "react-router-dom";

import Icons from "@/components/icons";
import ModernPageLayout from "@/components/layout/ModernPageLayout";
import { Button } from "@/components/ui/v2/button";
import { Card, CardContent } from "@/components/ui/v2/card";
import { Skeleton } from "@/components/ui/v2/skeleton";
import { useCampaign } from "@/features/campaigns/api/useCampaign";
import { useCampaignJobBoard } from "@/features/campaigns/jobboards/api/useCampaignJobBoard";
import CampaignIntegrationPauseButton from "@/features/campaigns/jobboards/components/CampaignIntegrationPauseButton";
import CampaignIntegrationStatusBadge from "@/features/campaigns/jobboards/components/CampaignIntegrationStatusBadge";
import {
  CampaignJobBoardDetailError,
  CampaignJobBoardMetrics,
  CampaignJobBoardMonthsSection,
} from "@/features/campaigns/jobboards/components/CampaignJobBoardDetail";
import {
  getCampaignDetailPath,
  getCampaignsPath,
  getEditCampaignIntegrationPath,
} from "@/features/campaigns/utils/navigation";
import { translateIntegrationDisplayName } from "@/features/integrations/utils";
import { useBreadcrumbs } from "@/lib/hooks/useBreadcrumbs";
import { usePermissionMatrix } from "@/lib/hooks/usePermissionMatrix";

function CampaignJobBoardDetailPage() {
  const { clientId, campaignId, clientIntegrationModuleId } = useParams();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const { canEdit } = usePermissionMatrix();

  const resolvedClientId = clientId ?? "";
  const resolvedCampaignId = campaignId ?? "";
  const resolvedClientIntegrationModuleId = clientIntegrationModuleId ?? "";

  const { data: campaign, isLoading: isCampaignLoading } = useCampaign({
    campaignId: resolvedCampaignId,
    clientId: resolvedClientId || undefined,
  });

  const { data: campaignJobBoard, isLoading: isJobBoardLoading } =
    useCampaignJobBoard({
      campaignId: resolvedCampaignId,
      clientIntegrationModuleId: resolvedClientIntegrationModuleId,
      clientId: resolvedClientId,
    });

  const campaignsPath = getCampaignsPath(clientId);
  const campaignDetailPath = getCampaignDetailPath(
    clientId,
    resolvedCampaignId
  );
  const editPath = getEditCampaignIntegrationPath(
    clientId,
    resolvedCampaignId,
    resolvedClientIntegrationModuleId
  );
  const destination = `${campaignDetailPath}${
    searchParams.toString() ? `?${searchParams.toString()}` : ""
  }`;

  const breadcrumbs = useBreadcrumbs({
    items: [
      { label: t("common.campaigns"), href: campaignsPath },
      { label: campaign?.name ?? t("common.campaign"), href: destination },
      { label: t("common.jobBoards") },
      { label: translateIntegrationDisplayName(campaignJobBoard?.integrationDisplayName ?? campaignJobBoard?.jobBoardName, t, i18n) || t("common.jobBoard") },
    ],
  });

  const isLoading = isCampaignLoading || isJobBoardLoading;
  const hasRequiredParams = Boolean(campaignId && clientIntegrationModuleId);

  if (!hasRequiredParams) {
    return null;
  }

  const canEditJobBoards = canEdit("jobBoards");
  const isExpired = campaignJobBoard?.isExpired;
  const canEditThisJobBoard = canEditJobBoards && !isExpired;

  return (
    <ModernPageLayout
      backbutton={{
        href: "../../",
        label: t("common.back"),
      }}
      breadcrumbs={breadcrumbs}
      title={translateIntegrationDisplayName(campaignJobBoard?.integrationDisplayName ?? campaignJobBoard?.jobBoardName, t, i18n) || ""}
      subtitle={campaign?.name ?? ""}
      badge={
        campaignJobBoard ? (
          <CampaignIntegrationStatusBadge
            campaignIntegration={campaignJobBoard}
            size="medium"
          />
        ) : undefined
      }
      actions={
        <>
          {canEditThisJobBoard && campaignJobBoard && (
            <CampaignIntegrationPauseButton
              campaignIntegration={campaignJobBoard}
              campaignId={resolvedCampaignId}
              clientId={resolvedClientId}
            />
          )}
          {canEditThisJobBoard && (
            <Button
              size="sm"
              asChild
              variant={"outline"}
              className="flex-1 h-9"
            >
              <Link to={editPath}>
                <Icons.EditSquareIcon className="h-4 w-4 mr-1.5" />
                <span className="hidden sm:inline">
                  {t("actions.edit", {
                    entity: t("common.jobBoard"),
                    defaultValue: "Edit Job Board",
                  })}
                </span>
                <span className="sm:inline lg:hidden">{t("common.edit")}</span>
              </Link>
            </Button>
          )}
        </>
      }
    >
      {!campaignJobBoard && !isLoading ? (
        <CampaignJobBoardDetailError />
      ) : (
        <div className="flex flex-col gap-6">
          {/* Key Metrics Cards */}
          <CampaignJobBoardMetrics
            startDate={campaignJobBoard?.startDate}
            endDate={campaignJobBoard?.endDate}
            monthlyBudget={campaignJobBoard?.monthlyBudget}
            isLoading={isLoading}
          />

          {/* Monthly Records Section */}
          {isLoading ? (
            <div className="flex flex-col gap-3 lg:gap-4">
              <div className="flex items-center justify-between">
                <Skeleton className="h-5 lg:h-6 w-24" />
                <Skeleton className="h-4 w-16" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Card key={i}>
                    <CardContent className="p-6">
                      <Skeleton className="h-4 w-24 mb-2" />
                      <Skeleton className="h-6 w-32" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ) : (
            <CampaignJobBoardMonthsSection
              months={campaignJobBoard?.monthlyRecords}
              campaignId={resolvedCampaignId}
              clientIntegrationModuleId={resolvedClientIntegrationModuleId}
              clientId={resolvedClientId}
              startDate={campaignJobBoard?.startDate}
            />
          )}
        </div>
      )}
    </ModernPageLayout>
  );
}

export default CampaignJobBoardDetailPage;
